/**
 * Headless selectors + formatters for the Insights → Product Usage tab.
 *
 * Platform-neutral (no DOM, no recharts) per the shared-core contract. The
 * server already does every aggregation (role bucketing, distinct-people
 * counts, route x role split, daily totals) — this file only adds display
 * labels and the two small client-side presentation derivations the endpoint
 * deliberately leaves out (freshness copy, a pages-chart row sort).
 */

import type {
  InsightsProductUsagePageRow,
  InsightsProductUsageRoleFilter,
  InsightsProductUsageWindow,
} from "../data/api-types";
import { formatDayLongMonthOrDash } from "./formatters";
import { shortDayLabel } from "./insights-daily-revenue";
import { roleLabel as businessRoleLabel } from "./roleLabels";

export { shortDayLabel };

/**
 * Label text for the three staff **buckets** when naming a group — the
 * role-comparison tiles, the filter control, and the pages chart's
 * legend/tooltip. "The same as Lauren's activity view but all staff, then
 * sales rep and CSR/AM filters" (product owner). One name per concept in
 * every layer (`naming.md`): every surface that names a bucket as a group
 * reads from this map rather than declaring its own copy. ``csr``'s label
 * comes from the single source of truth in ``roleLabels.ts`` (keeps
 * "CSR/AM" in sync with the rest of the app); ``sales_rep`` is pluralised
 * to match ("Sales reps", not the singular ``BusinessRole`` label) since
 * every surface here names a group of people, not one person; ``wider_team``
 * is a bucket this endpoint invents, not a real role, so it gets its own
 * copy. Indexable by any of the three concrete buckets (never ``all``).
 *
 * This is deliberately NOT used for the staff table's Role column, which
 * shows each person's real, un-bucketed role (``InsightsProductUsageUserRow
 * .business_role``) via ``roleLabels.ts``'s own ``roleLabel`` instead — the
 * bucket and the real role are different fields for different jobs (see
 * that field's docstring), so a "wider_team" row must not render as
 * "Everyone else" in the one place the tab shows what someone actually is.
 */
export const ROLE_FILTER_LABELS: Record<
  InsightsProductUsageRoleFilter,
  string
> = {
  all: "All staff",
  sales_rep: "Sales reps",
  csr: businessRoleLabel("csr"),
  wider_team: "Everyone else",
};

export const ROLE_FILTER_OPTIONS: InsightsProductUsageRoleFilter[] = [
  "all",
  "sales_rep",
  "csr",
  "wider_team",
];

export const WINDOW_LABELS: Record<InsightsProductUsageWindow, string> = {
  this_month: "This month",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
};

export const WINDOW_OPTIONS: InsightsProductUsageWindow[] = [
  "this_month",
  "last_30_days",
  "last_90_days",
];

/**
 * "Data as of 23 Sep 2026" — or a clear "no data yet" copy when the dbt
 * build has never run. The dbt build is daily, not live (spec §7): this
 * label is how the tab keeps that honest rather than implying real time.
 */
export function freshnessLabel(
  latestActivityDate: string | null | undefined,
): string {
  if (!latestActivityDate) return "No usage data yet";
  return `Data as of ${formatDayLongMonthOrDash(latestActivityDate)}`;
}

/** Route x role rows, sorted by total views descending — the chart's read order. */
export function sortPagesByTotal(
  pages: InsightsProductUsagePageRow[],
): InsightsProductUsagePageRow[] {
  return [...pages].sort((a, b) => b.total_views - a.total_views);
}
